/**
 * Ragdoll — a jointed articulated body solved with position-based dynamics.
 *
 * ============================================================================
 *  SKELETON SPEC — the exact shape `physics.makeRagdoll(spec)` accepts.
 *  (AI team: everything is optional. `physics.makeRagdoll()` with no argument
 *   gives you a 1.8 m humanoid standing at the origin.)
 * ============================================================================
 *
 *   spec = {
 *     // --- placement ---
 *     position:   THREE.Vector3 | [x,y,z],       // world position of the HIP joint
 *     quaternion: THREE.Quaternion | [x,y,z,w],  // world orientation of the whole rig
 *     scale:      1,                              // uniform scale (1 => 1.8 m tall)
 *     velocity:   THREE.Vector3 | [x,y,z],       // initial velocity for every particle
 *     angularKick: 0,                             // random-ish spin magnitude (seeded)
 *     seed:       1337,                           // deterministic RNG seed
 *     mask:       physics.MASK.WORLD,             // what the ragdoll collides with
 *     enabled:    true,
 *
 *     // --- optional overrides; omit for the default humanoid ---
 *     joints: {                 // LOCAL joint positions, metres, +Y up, facing +Z
 *       hip:[0,0,0], spineMid:[0,0.22,0], neck:[0,0.50,0], headTop:[0,0.74,0],
 *       shoulderL:[-0.19,0.46,0], elbowL:[-0.19,0.18,0], handL:[-0.19,-0.08,0],
 *       shoulderR:[ 0.19,0.46,0], elbowR:[ 0.19,0.18,0], handR:[ 0.19,-0.08,0],
 *       hipL:[-0.10,-0.02,0], kneeL:[-0.11,-0.46,0], footL:[-0.11,-0.86,0],
 *       hipR:[ 0.10,-0.02,0], kneeR:[ 0.11,-0.46,0], footR:[ 0.11,-0.86,0],
 *       hipFront:[0,0.02,0.14], chestFront:[0,0.48,0.13],   // orientation reference
 *     },
 *     bones: [                  // each bone is a capsule between two joints
 *       { name:'pelvis',    from:'hip',       to:'spineMid', radius:0.15, mass:12 },
 *       { name:'spine',     from:'spineMid',  to:'neck',     radius:0.14, mass:14 },
 *       { name:'head',      from:'neck',      to:'headTop',  radius:0.11, mass:5  },
 *       { name:'upperArmL', from:'shoulderL', to:'elbowL',   radius:0.06, mass:2.2 },
 *       { name:'lowerArmL', from:'elbowL',    to:'handL',    radius:0.05, mass:1.6 },
 *       { name:'upperArmR', ... }, { name:'lowerArmR', ... },
 *       { name:'thighL',    from:'hipL',      to:'kneeL',    radius:0.09, mass:7  },
 *       { name:'shinL',     from:'kneeL',     to:'footL',    radius:0.07, mass:4  },
 *       { name:'thighR', ... }, { name:'shinR', ... },
 *     ],
 *     limits: [                 // cone-twist style limits, radians
 *       // `swing` is the HALF-ANGLE of a cone centred on the joint's REST angle
 *       // (measured from the spawn pose), NOT on the parent bone axis. A hanging
 *       // arm sits ~140° from the clavicle direction, so an absolute cone would
 *       // be violated by the spawn pose itself.
 *       // `maxBend` is the hardest fold the joint may reach (0 = straight);
 *       // use it on elbows and knees. (`minFold` is accepted as an alias.)
 *       { joint:'spineMid', parent:'pelvis',    child:'spine',     swing:0.50, twist:0.45 },
 *       { joint:'shoulderL',parent:'spine',     child:'upperArmL', swing:1.30, twist:1.0  },
 *       { joint:'elbowL',   parent:'upperArmL', child:'lowerArmL', swing:2.40, maxBend:2.35 },
 *       { joint:'kneeL',    parent:'thighL',    child:'shinL',     swing:2.20, maxBend:2.20 },
 *       ...
 *     ],
 *     links: [ ['neck','shoulderL',1], ... ],   // extra rigid distance links
 *   }
 *
 *   NOTE (honest limitation): bones are two-particle segments, so they carry no
 *   twist degree of freedom of their own. `twist` currently only stiffens the
 *   torso girdle; limb roll is unconstrained. The torso DOES have a real frame
 *   (via the hipFront / chestFront reference particles), which is what keeps the
 *   returned bone quaternions from spinning.
 *
 * ============================================================================
 *  RETURNED HANDLE — what the AI team drives on death
 * ============================================================================
 *
 *   ragdoll = {
 *     boneNames:  string[],                    // stable order
 *     bones:      [{ name, position:Vector3, quaternion:Quaternion,
 *                    length, radius, mass }],  // WORLD transforms, refreshed every fixed step
 *     bone(name)              -> the entry above, or null
 *     getBoneTransform(name, outPos, outQuat) -> bool
 *     boneMatrix(name, outMatrix4)            -> Matrix4|null
 *     applyImpulse(boneName, impulse:Vector3, point?:Vector3)  // N·s, world space
 *     setVelocity(v:Vector3)
 *     centre:      Vector3                     // pelvis-ish centre of mass, refreshed
 *     sleeping:    boolean
 *     enabled:     boolean                     // set false to freeze
 *     wake()
 *     dispose()                                // unregisters from the physics system
 *   }
 *
 *   Bone position is the CENTRE of the bone capsule; `quaternion` maps local +Y
 *   onto the bone axis (root -> tip) with a stabilised forward reference, so a
 *   skinned mesh authored with bones along +Y drops straight in.
 *
 * owner: physics
 */
import * as THREE from 'three';
import { makeRng } from '../core/Rand.js';
import { segTriDistance } from './PhysMath.js';

const DEFAULT_JOINTS = {
  hip: [0, 0, 0],
  spineMid: [0, 0.22, 0],
  neck: [0, 0.50, 0],
  headTop: [0, 0.74, 0],
  shoulderL: [-0.19, 0.46, 0],
  elbowL: [-0.19, 0.18, 0],
  handL: [-0.19, -0.08, 0],
  shoulderR: [0.19, 0.46, 0],
  elbowR: [0.19, 0.18, 0],
  handR: [0.19, -0.08, 0],
  hipL: [-0.10, -0.02, 0],
  kneeL: [-0.11, -0.46, 0],
  footL: [-0.11, -0.86, 0],
  hipR: [0.10, -0.02, 0],
  kneeR: [0.11, -0.46, 0],
  footR: [0.11, -0.86, 0],
  hipFront: [0, 0.02, 0.14],
  chestFront: [0, 0.48, 0.13],
};

const DEFAULT_BONES = [
  { name: 'pelvis', from: 'hip', to: 'spineMid', radius: 0.15, mass: 12 },
  { name: 'spine', from: 'spineMid', to: 'neck', radius: 0.14, mass: 14 },
  { name: 'head', from: 'neck', to: 'headTop', radius: 0.11, mass: 5 },
  { name: 'upperArmL', from: 'shoulderL', to: 'elbowL', radius: 0.06, mass: 2.2 },
  { name: 'lowerArmL', from: 'elbowL', to: 'handL', radius: 0.05, mass: 1.6 },
  { name: 'upperArmR', from: 'shoulderR', to: 'elbowR', radius: 0.06, mass: 2.2 },
  { name: 'lowerArmR', from: 'elbowR', to: 'handR', radius: 0.05, mass: 1.6 },
  { name: 'thighL', from: 'hipL', to: 'kneeL', radius: 0.09, mass: 7 },
  { name: 'shinL', from: 'kneeL', to: 'footL', radius: 0.07, mass: 4 },
  { name: 'thighR', from: 'hipR', to: 'kneeR', radius: 0.09, mass: 7 },
  { name: 'shinR', from: 'kneeR', to: 'footR', radius: 0.07, mass: 4 },
];

// Rigid links that hold the shoulder / pelvis girdles and the orientation
// reference particles together. [a, b, stiffness]
// Each anchored particle gets exactly THREE links, which determines its
// position without over-constraining the solve. Over-determined girdles never
// converge, and the leftover residual is what stops a ragdoll from sleeping.
const DEFAULT_LINKS = [
  ['neck', 'shoulderL', 1], ['neck', 'shoulderR', 1], ['shoulderL', 'shoulderR', 1],
  ['spineMid', 'shoulderL', 1], ['spineMid', 'shoulderR', 1],
  ['hip', 'hipL', 1], ['hip', 'hipR', 1], ['hipL', 'hipR', 1],
  ['spineMid', 'hipL', 1], ['spineMid', 'hipR', 1],
  ['hip', 'hipFront', 1], ['hipL', 'hipFront', 1], ['hipR', 'hipFront', 1],
  ['neck', 'chestFront', 1], ['shoulderL', 'chestFront', 1], ['shoulderR', 'chestFront', 1],
];

// `swing`   = half-angle of the allowed cone, measured AROUND the rest angle.
// `maxBend`  = hardest fold the joint may reach, radians (0 = straight).
const DEFAULT_LIMITS = [
  { joint: 'spineMid', parent: 'pelvis', child: 'spine', swing: 0.50, twist: 0.45 },
  { joint: 'neck', parent: 'spine', child: 'head', swing: 0.65, twist: 0.7 },
  { joint: 'shoulderL', parent: 'spine', child: 'upperArmL', swing: 1.30, twist: 1.0 },
  { joint: 'shoulderR', parent: 'spine', child: 'upperArmR', swing: 1.30, twist: 1.0 },
  { joint: 'elbowL', parent: 'upperArmL', child: 'lowerArmL', swing: 2.40, maxBend: 2.35 },
  { joint: 'elbowR', parent: 'upperArmR', child: 'lowerArmR', swing: 2.40, maxBend: 2.35 },
  { joint: 'hipL', parent: 'pelvis', child: 'thighL', swing: 1.10, twist: 0.6 },
  { joint: 'hipR', parent: 'pelvis', child: 'thighR', swing: 1.10, twist: 0.6 },
  { joint: 'kneeL', parent: 'thighL', child: 'shinL', swing: 2.20, maxBend: 2.20 },
  { joint: 'kneeR', parent: 'thighR', child: 'shinR', swing: 2.20, maxBend: 2.20 },
];

const SUBSTEPS = 4;
const SOLVER_ITERS = 3;
const SLEEP_SPEED = 0.22;
const SLEEP_TIME = 0.5;
const MAX_PARTICLE_SPEED = 13;
const MAX_COLL_CORR = 0.06;   // metres of depenetration per substep, per particle

export class Ragdoll {
  constructor(queries, spec = {}, gravity = -(9.81 * 2.2)) {
    this.queries = queries;
    this.gravity = gravity;
    this.enabled = spec.enabled !== false;
    this.mask = spec.mask ?? 1;
    this.sleeping = false;
    this._sleepTimer = 0;
    this.rng = makeRng(spec.seed ?? 1337);
    this.disposed = false;

    const scale = spec.scale ?? 1;
    const jointDefs = { ...DEFAULT_JOINTS, ...(spec.joints || {}) };
    const boneDefs = spec.bones || DEFAULT_BONES;
    const limitDefs = spec.limits || (spec.bones ? [] : DEFAULT_LIMITS);
    const linkDefs = spec.links || (spec.bones ? [] : DEFAULT_LINKS);

    // ---- particles ----
    this.jointNames = Object.keys(jointDefs);
    const n = this.jointNames.length;
    this.count = n;
    this.index = new Map(this.jointNames.map((k, i) => [k, i]));
    this.pos = new Float32Array(n * 3);
    this.prev = new Float32Array(n * 3);
    this.vel = new Float32Array(n * 3);
    this.invMass = new Float32Array(n);
    this.pRadius = new Float32Array(n);
    this.grounded = new Uint8Array(n);
    // Collision pushes are POSITIONAL only. If they were allowed to feed the
    // (p - prev)/h velocity update, a 0.15 m depenetration at h = 1/360 would
    // launch a limb at 54 m/s — that is what makes naive PBD ragdolls explode.
    this.collCorr = new Float32Array(n * 3);
    this.contactN = new Float32Array(n * 3);
    this._corr = new Float32Array(n * 3);
    this._prevKE = 0;
    this._watchPos = new Float32Array(n * 3);
    this._watchTimer = 0;

    const q = _q.copy(toQuat(spec.quaternion));
    const origin = toVec(spec.position, _v0);
    for (let i = 0; i < n; i++) {
      const j = jointDefs[this.jointNames[i]];
      _v1.set(j[0] * scale, j[1] * scale, j[2] * scale).applyQuaternion(q).add(origin);
      this.pos[i * 3] = _v1.x; this.pos[i * 3 + 1] = _v1.y; this.pos[i * 3 + 2] = _v1.z;
    }

    // ---- bones ----
    this.boneNames = boneDefs.map((b) => b.name);
    this.boneCount = boneDefs.length;
    this.boneA = new Int32Array(this.boneCount);
    this.boneB = new Int32Array(this.boneCount);
    this.boneRest = new Float32Array(this.boneCount);
    this.boneRadius = new Float32Array(this.boneCount);
    this.boneMass = new Float32Array(this.boneCount);
    this.boneIndex = new Map();
    const mass = new Float32Array(n);

    for (let i = 0; i < this.boneCount; i++) {
      const d = boneDefs[i];
      const a = this.index.get(d.from), b = this.index.get(d.to);
      if (a === undefined || b === undefined) throw new Error(`Ragdoll bone "${d.name}" references an unknown joint`);
      this.boneA[i] = a; this.boneB[i] = b;
      this.boneRest[i] = dist(this.pos, a, b);
      this.boneRadius[i] = (d.radius ?? 0.08) * scale;
      this.boneMass[i] = (d.mass ?? 3) * (scale * scale * scale);
      this.boneIndex.set(d.name, i);
      mass[a] += this.boneMass[i] * 0.5;
      mass[b] += this.boneMass[i] * 0.5;
      this.pRadius[a] = Math.max(this.pRadius[a], this.boneRadius[i]);
      this.pRadius[b] = Math.max(this.pRadius[b], this.boneRadius[i]);
    }
    for (let i = 0; i < n; i++) {
      if (mass[i] <= 0) { mass[i] = 0.6; this.pRadius[i] = this.pRadius[i] || 0.05 * scale; }
      this.invMass[i] = 1 / mass[i];
    }
    this.mass = mass;
    this.totalMass = mass.reduce((a, b) => a + b, 0);

    // ---- extra rigid links ----
    this.linkA = []; this.linkB = []; this.linkRest = []; this.linkK = [];
    for (const [a, b, k] of linkDefs) {
      const ia = this.index.get(a), ib = this.index.get(b);
      if (ia === undefined || ib === undefined) continue;
      this.linkA.push(ia); this.linkB.push(ib);
      this.linkRest.push(dist(this.pos, ia, ib));
      this.linkK.push(k ?? 1);
    }

    // ---- cone / fold limits ----
    // `swing` is the half-angle of a cone centred on the joint's REST angle,
    // not on the parent bone axis: a hanging arm sits ~140° from the clavicle
    // direction, so an absolute cone would be violated by the spawn pose and
    // fire a huge correction on frame 1.
    this.limits = [];
    for (const l of limitDefs) {
      const pb = this.boneIndex.get(l.parent), cb = this.boneIndex.get(l.child);
      if (pb === undefined || cb === undefined) continue;
      const a = this.boneA[pb];
      const j = this.index.get(l.joint) ?? this.boneB[pb];
      const b = this.boneB[cb];
      const rest = angleBetween(this.pos, a, j, b);
      const swing = l.swing ?? 1.2;
      this.limits.push({
        a, j, b,
        swing,
        twist: l.twist ?? 0.8,
        maxBend: l.maxBend ?? l.minFold ?? 0,
        restAngle: rest,
        maxAngle: Math.min(Math.PI - 1e-3, rest + swing),
        minAngle: Math.max(1e-3, rest - swing),
      });
    }

    // ---- public bone transforms ----
    this.bones = [];
    for (let i = 0; i < this.boneCount; i++) {
      this.bones.push({
        name: this.boneNames[i],
        position: new THREE.Vector3(),
        quaternion: new THREE.Quaternion(),
        length: this.boneRest[i],
        radius: this.boneRadius[i],
        mass: this.boneMass[i],
      });
    }
    this.centre = new THREE.Vector3();

    // ---- initial motion ----
    const v = toVec(spec.velocity, _v1);
    for (let i = 0; i < n; i++) {
      this.vel[i * 3] = v.x; this.vel[i * 3 + 1] = v.y; this.vel[i * 3 + 2] = v.z;
    }
    const kick = spec.angularKick ?? 0;
    if (kick > 0) {
      for (let i = 0; i < n; i++) {
        this.vel[i * 3] += (this.rng() - 0.5) * kick;
        this.vel[i * 3 + 1] += (this.rng() - 0.5) * kick;
        this.vel[i * 3 + 2] += (this.rng() - 0.5) * kick;
      }
    }
    this.refreshTransforms();
  }

  bone(name) {
    const i = this.boneIndex.get(name);
    return i === undefined ? null : this.bones[i];
  }

  getBoneTransform(name, outPos, outQuat) {
    const b = this.bone(name);
    if (!b) return false;
    if (outPos) outPos.copy(b.position);
    if (outQuat) outQuat.copy(b.quaternion);
    return true;
  }

  boneMatrix(name, out = new THREE.Matrix4()) {
    const b = this.bone(name);
    if (!b) return null;
    return out.compose(b.position, b.quaternion, _one);
  }

  jointPosition(name, out = new THREE.Vector3()) {
    const i = this.index.get(name);
    if (i === undefined) return null;
    return out.set(this.pos[i * 3], this.pos[i * 3 + 1], this.pos[i * 3 + 2]);
  }

  _sleep() {
    this.sleeping = true;
    this.vel.fill(0);
    this._prevKE = 0;
  }

  wake() {
    this.sleeping = false;
    this._sleepTimer = 0;
    this._watchTimer = 0;
    if (this._watchPos) this._watchPos.set(this.pos);
    this._prevKE = -1;   // let the energy guard re-latch after an external impulse
  }

  /** Apply a world-space impulse (N·s) to a bone, optionally at a world point. */
  applyImpulse(boneName, impulse, point) {
    const bi = this.boneIndex.get(boneName);
    if (bi === undefined) return false;
    const a = this.boneA[bi], b = this.boneB[bi];
    let t = 0.5;
    if (point) {
      _v0.set(this.pos[a * 3], this.pos[a * 3 + 1], this.pos[a * 3 + 2]);
      _v1.set(this.pos[b * 3], this.pos[b * 3 + 1], this.pos[b * 3 + 2]).sub(_v0);
      const len2 = _v1.lengthSq() || 1;
      t = Math.max(0, Math.min(1, _v2.copy(point).sub(_v0).dot(_v1) / len2));
    }
    const wa = (1 - t), wb = t;
    this.vel[a * 3] += impulse.x * wa * this.invMass[a];
    this.vel[a * 3 + 1] += impulse.y * wa * this.invMass[a];
    this.vel[a * 3 + 2] += impulse.z * wa * this.invMass[a];
    this.vel[b * 3] += impulse.x * wb * this.invMass[b];
    this.vel[b * 3 + 1] += impulse.y * wb * this.invMass[b];
    this.vel[b * 3 + 2] += impulse.z * wb * this.invMass[b];
    this.wake();
    return true;
  }

  setVelocity(v) {
    for (let i = 0; i < this.count; i++) {
      this.vel[i * 3] = v.x; this.vel[i * 3 + 1] = v.y; this.vel[i * 3 + 2] = v.z;
    }
    this.wake();
  }

  /* ------------------------------------------------------------------ *
   * Simulation
   * ------------------------------------------------------------------ */
  step(dt) {
    if (!this.enabled || this.sleeping || this.disposed) return;
    const h = dt / SUBSTEPS;
    for (let s = 0; s < SUBSTEPS; s++) this._substep(h);

    // sleep detection
    let maxSpeed = 0;
    for (let i = 0; i < this.count; i++) {
      const sp = this.vel[i * 3] ** 2 + this.vel[i * 3 + 1] ** 2 + this.vel[i * 3 + 2] ** 2;
      if (sp > maxSpeed) maxSpeed = sp;
    }
    if (maxSpeed < SLEEP_SPEED * SLEEP_SPEED) {
      this._sleepTimer += dt;
      if (this._sleepTimer > SLEEP_TIME) this._sleep();
    } else this._sleepTimer = 0;

    // Displacement watchdog. A wedged limb can hold a small limit cycle whose
    // velocity never drops under the threshold even though nothing actually
    // moves. If no particle has travelled more than a centimetre in half a
    // second, the ragdoll is at rest whatever its instantaneous velocity says.
    if (!this.sleeping) {
      this._watchTimer += dt;
      if (this._watchTimer >= SLEEP_TIME) {
        let moved = 0;
        const w = this._watchPos;
        for (let i = 0; i < this.count; i++) {
          const o = i * 3;
          const d = Math.hypot(this.pos[o] - w[o], this.pos[o + 1] - w[o + 1], this.pos[o + 2] - w[o + 2]);
          if (d > moved) moved = d;
        }
        if (moved < 0.012) this._sleep();
        w.set(this.pos);
        this._watchTimer = 0;
      }
    }

    this.refreshTransforms();
  }

  _substep(h) {
    const n = this.count, pos = this.pos, prev = this.prev, vel = this.vel;
    // predict
    for (let i = 0; i < n; i++) {
      const o = i * 3;
      vel[o + 1] += this.gravity * h;
      // mild damping keeps limbs from whipping
      vel[o] *= 0.999; vel[o + 1] *= 0.999; vel[o + 2] *= 0.999;
      prev[o] = pos[o]; prev[o + 1] = pos[o + 1]; prev[o + 2] = pos[o + 2];
      pos[o] += vel[o] * h;
      pos[o + 1] += vel[o + 1] * h;
      pos[o + 2] += vel[o + 2] * h;
    }

    this.grounded.fill(0);
    this.collCorr.fill(0);
    this.contactN.fill(0);
    for (let it = 0; it < SOLVER_ITERS; it++) {
      this._solveDistance();
      this._solveLimits();
      this._solveCollisions();
    }

    // Velocity from the positional change, MINUS the collision correction.
    const invH = 1 / h;
    const cc = this.collCorr, cn = this.contactN;
    for (let i = 0; i < n; i++) {
      const o = i * 3;
      let vx = (pos[o] - prev[o] - cc[o]) * invH;
      let vy = (pos[o + 1] - prev[o + 1] - cc[o + 1]) * invH;
      let vz = (pos[o + 2] - prev[o + 2] - cc[o + 2]) * invH;
      if (this.grounded[i]) {
        // kill the into-surface component (bodies do not bounce much) and add
        // Coulomb-ish sliding friction along the contact plane
        let nx = cn[o], ny = cn[o + 1], nz = cn[o + 2];
        const nl = Math.hypot(nx, ny, nz);
        if (nl > 1e-6) {
          nx /= nl; ny /= nl; nz /= nl;
          const vn = vx * nx + vy * ny + vz * nz;
          if (vn < 0) {
            const k = vn * 1.05;         // 5% restitution
            vx -= nx * k; vy -= ny * k; vz -= nz * k;
          }
        }
        vx *= 0.55; vy *= 0.86; vz *= 0.55;
      }
      // Safety clamp: a real limb never exceeds ~18 m/s, and a runaway
      // constraint must never be able to fling a bone across the level.
      const sp = Math.hypot(vx, vy, vz);
      if (sp > MAX_PARTICLE_SPEED) {
        const s = MAX_PARTICLE_SPEED / sp;
        vx *= s; vy *= s; vz *= s;
      }
      vel[o] = vx; vel[o + 1] = vy; vel[o + 2] = vz;
    }

    // ---- energy guard ----
    // A passive ragdoll can only gain kinetic energy from the work gravity does
    // on it; joints and contacts strictly remove energy. Anything above that is
    // solver noise, so scale it away. This is what stops a limb wedged in a
    // stair corner from spasming forever, and it costs one pass over 18 floats.
    // While any bone is touching the world the ragdoll is only ever losing
    // energy, so its kinetic energy must not rise. (In free flight it must, so
    // the guard is off then.) `_prevKE < 0` disables the guard for one substep
    // after wake()/applyImpulse so an external impulse is not scaled away.
    let ke = 0, touching = 0;
    for (let i = 0; i < n; i++) {
      const o = i * 3;
      ke += this.mass[i] * (vel[o] * vel[o] + vel[o + 1] * vel[o + 1] + vel[o + 2] * vel[o + 2]);
      if (this.grounded[i]) touching++;
    }
    ke *= 0.5;
    if (touching > 0 && this._prevKE >= 0 && ke > this._prevKE && ke > 1e-6) {
      const s = Math.sqrt(this._prevKE / ke);
      for (let i = 0; i < n * 3; i++) vel[i] *= s;
      ke = this._prevKE;
    }
    this._prevKE = ke;
  }

  _solveDistance() {
    const pos = this.pos, iw = this.invMass;
    for (let i = 0; i < this.boneCount; i++) {
      solveDist(pos, iw, this.boneA[i], this.boneB[i], this.boneRest[i], 1);
    }
    for (let i = 0; i < this.linkA.length; i++) {
      solveDist(pos, iw, this.linkA[i], this.linkB[i], this.linkRest[i], this.linkK[i]);
    }
  }

  _solveLimits() {
    const pos = this.pos, iw = this.invMass;
    for (const L of this.limits) {
      // --- cone (swing) limit between the parent and child bone directions ---
      const a = L.a * 3, j = L.j * 3, b = L.b * 3;
      let d1x = pos[j] - pos[a], d1y = pos[j + 1] - pos[a + 1], d1z = pos[j + 2] - pos[a + 2];
      let d2x = pos[b] - pos[j], d2y = pos[b + 1] - pos[j + 1], d2z = pos[b + 2] - pos[j + 2];
      const l1 = Math.hypot(d1x, d1y, d1z), l2 = Math.hypot(d2x, d2y, d2z);
      if (l1 < 1e-6 || l2 < 1e-6) continue;
      d1x /= l1; d1y /= l1; d1z /= l1;
      d2x /= l2; d2y /= l2; d2z /= l2;
      const cos = Math.max(-1, Math.min(1, d1x * d2x + d1y * d2y + d1z * d2z));
      const angle = Math.acos(cos);

      if (angle > L.maxAngle || angle < L.minAngle) {
        const target = angle > L.maxAngle ? L.maxAngle : L.minAngle;
        // rotate d2 toward/away from d1 until the angle equals the limit
        let ax = d1y * d2z - d1z * d2y, ay = d1z * d2x - d1x * d2z, az = d1x * d2y - d1y * d2x;
        let al = Math.hypot(ax, ay, az);
        if (al < 1e-6) {
          // collinear — any perpendicular axis will do
          if (Math.abs(d1x) < 0.9) { ax = 0; ay = d1z; az = -d1y; }
          else { ax = d1z; ay = 0; az = -d1x; }
          al = Math.hypot(ax, ay, az) || 1;
        }
        ax /= al; ay /= al; az /= al;
        // Rodrigues: rotate d1 by `target` about the axis
        const c = Math.cos(target), s = Math.sin(target);
        const cx = ay * d1z - az * d1y, cy = az * d1x - ax * d1z, cz = ax * d1y - ay * d1x;
        const dot = ax * d1x + ay * d1y + az * d1z;
        const tx = d1x * c + cx * s + ax * dot * (1 - c);
        const ty = d1y * c + cy * s + ay * dot * (1 - c);
        const tz = d1z * c + cz * s + az * dot * (1 - c);
        // desired tip position
        const wx = pos[j] + tx * l2 - pos[b];
        const wy = pos[j + 1] + ty * l2 - pos[b + 1];
        const wz = pos[j + 2] + tz * l2 - pos[b + 2];
        // (wx,wy,wz) is the vector from the current tip to the on-cone tip.
        // Move the child tip most of the way and nudge the parent root back so
        // the joint does not act as an infinite momentum source.
        // Inverse-mass weighted and SYMMETRIC — an asymmetric split injects
        // linear momentum every iteration and turns the ragdoll into a rocket.
        const k = 0.45;
        const wa = iw[L.a], wb = iw[L.b], sum = wa + wb || 1;
        const kb = k * (wb / sum), ka = k * (wa / sum);
        pos[b] += wx * kb; pos[b + 1] += wy * kb; pos[b + 2] += wz * kb;
        pos[a] -= wx * ka; pos[a + 1] -= wy * ka; pos[a + 2] -= wz * ka;
      }

      // --- fold limit (elbows / knees cannot close past maxBend) ---
      if (L.maxBend > 0) {
        const minDist = Math.sqrt(l1 * l1 + l2 * l2 + 2 * l1 * l2 * Math.cos(L.maxBend));
        const dx = pos[b] - pos[a], dy = pos[b + 1] - pos[a + 1], dz = pos[b + 2] - pos[a + 2];
        const d = Math.hypot(dx, dy, dz);
        if (d > 1e-6 && d < minDist) {
          const corr = (minDist - d) / d * 0.5;
          const wa = iw[L.a], wb = iw[L.b], sum = wa + wb || 1;
          pos[a] -= dx * corr * (wa / sum); pos[a + 1] -= dy * corr * (wa / sum); pos[a + 2] -= dz * corr * (wa / sum);
          pos[b] += dx * corr * (wb / sum); pos[b + 1] += dy * corr * (wb / sum); pos[b + 2] += dz * corr * (wb / sum);
        }
      }
    }
  }

  /**
   * Push bone capsules out of the static world.
   *
   * Corrections are accumulated per particle with a max-projection rule (never
   * summed) and clamped, then applied in one Jacobi pass. Summing the pushes
   * from every triangle of a stair tread would teleport a limb and the distance
   * constraints would then convert that teleport into tens of m/s next substep.
   */
  _solveCollisions() {
    const q = this.queries;
    const bvh = q.bvh;
    if (!bvh || bvh.triCount === 0) return;
    const pos = this.pos, iw = this.invMass;
    const corr = this._corr;
    corr.fill(0);

    for (let i = 0; i < this.boneCount; i++) {
      const ia = this.boneA[i], ib = this.boneB[i];
      const r = this.boneRadius[i];
      const ax = pos[ia * 3], ay = pos[ia * 3 + 1], az = pos[ia * 3 + 2];
      const bx = pos[ib * 3], by = pos[ib * 3 + 1], bz = pos[ib * 3 + 2];
      const pad = r + 0.01;
      const cnt = q._gather(
        Math.min(ax, bx) - pad, Math.min(ay, by) - pad, Math.min(az, bz) - pad,
        Math.max(ax, bx) + pad, Math.max(ay, by) + pad, Math.max(az, bz) + pad,
        this.mask,
      );
      if (cnt === 0) continue;
      const cand = q._cand, P = bvh.pos;
      for (let c = 0; c < cnt; c++) {
        const tri = cand[c], o = tri * 9;
        const d = segTriDistance(
          pos[ia * 3], pos[ia * 3 + 1], pos[ia * 3 + 2],
          pos[ib * 3], pos[ib * 3 + 1], pos[ib * 3 + 2],
          P[o], P[o + 1], P[o + 2], P[o + 3], P[o + 4], P[o + 5], P[o + 6], P[o + 7], P[o + 8],
          _segP, _triP,
        );
        if (d >= r) continue;
        let nx, ny, nz;
        if (d > 1e-6) { nx = (_segP.x - _triP.x) / d; ny = (_segP.y - _triP.y) / d; nz = (_segP.z - _triP.z) / d; }
        else { const n3 = tri * 3; nx = bvh.nrm[n3]; ny = bvh.nrm[n3 + 1]; nz = bvh.nrm[n3 + 2]; }
        const push = r - d;
        // distribute along the segment by where the contact landed
        const segLen2 = (bx - ax) ** 2 + (by - ay) ** 2 + (bz - az) ** 2 || 1;
        const t = clamp01(((_segP.x - ax) * (bx - ax) + (_segP.y - ay) * (by - ay) + (_segP.z - az) * (bz - az)) / segLen2);
        const wa = iw[ia] * (1 - t), wb = iw[ib] * t;
        const sum = wa + wb;
        if (sum <= 1e-9) continue;
        const ka = push * (wa / sum), kb = push * (wb / sum);
        accumulatePush(corr, ia, nx, ny, nz, ka);
        accumulatePush(corr, ib, nx, ny, nz, kb);
        this.grounded[ia] = 1; this.grounded[ib] = 1;
      }
    }

    const cc = this.collCorr, cn = this.contactN;
    for (let i = 0; i < this.count; i++) {
      const o = i * 3;
      let cx = corr[o], cy = corr[o + 1], cz = corr[o + 2];
      const len = Math.hypot(cx, cy, cz);
      if (len < 1e-9) { this.grounded[i] = 0; continue; }
      // the net push direction IS the contact normal for the velocity pass
      cn[o] = cx / len; cn[o + 1] = cy / len; cn[o + 2] = cz / len;
      if (len > MAX_COLL_CORR) { const s = MAX_COLL_CORR / len; cx *= s; cy *= s; cz *= s; }
      pos[o] += cx; pos[o + 1] += cy; pos[o + 2] += cz;
      cc[o] += cx; cc[o + 1] += cy; cc[o + 2] += cz;
    }
  }

  /** Rebuild the public per-bone world transforms. Called after every step. */
  refreshTransforms() {
    const pos = this.pos;
    // torso reference frame — gives limbs a stable, non-spinning roll
    const fwd = _fwd;
    const hipI = this.index.get('hip'), frontI = this.index.get('hipFront');
    if (hipI !== undefined && frontI !== undefined) {
      fwd.set(pos[frontI * 3] - pos[hipI * 3], pos[frontI * 3 + 1] - pos[hipI * 3 + 1], pos[frontI * 3 + 2] - pos[hipI * 3 + 2]);
      if (fwd.lengthSq() < 1e-9) fwd.set(0, 0, 1);
      fwd.normalize();
    } else fwd.set(0, 0, 1);

    for (let i = 0; i < this.boneCount; i++) {
      const a = this.boneA[i] * 3, b = this.boneB[i] * 3;
      const out = this.bones[i];
      out.position.set((pos[a] + pos[b]) * 0.5, (pos[a + 1] + pos[b + 1]) * 0.5, (pos[a + 2] + pos[b + 2]) * 0.5);
      _axis.set(pos[b] - pos[a], pos[b + 1] - pos[a + 1], pos[b + 2] - pos[a + 2]);
      const len = _axis.length();
      out.length = len;
      if (len < 1e-6) continue;
      _axis.multiplyScalar(1 / len);
      // build an orthonormal basis: +Y = bone axis, +Z ≈ torso forward
      _right.crossVectors(fwd, _axis);
      if (_right.lengthSq() < 1e-8) _right.crossVectors(_up, _axis);
      if (_right.lengthSq() < 1e-8) _right.set(1, 0, 0);
      _right.normalize();
      _front.crossVectors(_axis, _right).normalize();
      _m.makeBasis(_right, _axis, _front);
      out.quaternion.setFromRotationMatrix(_m);
    }

    // centre of mass
    let cx = 0, cy = 0, cz = 0;
    for (let i = 0; i < this.count; i++) {
      const m = this.mass[i];
      cx += pos[i * 3] * m; cy += pos[i * 3 + 1] * m; cz += pos[i * 3 + 2] * m;
    }
    this.centre.set(cx / this.totalMass, cy / this.totalMass, cz / this.totalMass);
  }

  dispose() {
    this.disposed = true;
    this.enabled = false;
    if (this._onDispose) this._onDispose(this);
  }
}

/* ---- helpers ---- */
function solveDist(pos, iw, a, b, rest, k) {
  const ax = a * 3, bx = b * 3;
  const dx = pos[bx] - pos[ax], dy = pos[bx + 1] - pos[ax + 1], dz = pos[bx + 2] - pos[ax + 2];
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (d < 1e-9) return;
  const wa = iw[a], wb = iw[b], sum = wa + wb;
  if (sum <= 1e-9) return;
  const corr = ((d - rest) / d) * k;
  pos[ax] += dx * corr * (wa / sum);
  pos[ax + 1] += dy * corr * (wa / sum);
  pos[ax + 2] += dz * corr * (wa / sum);
  pos[bx] -= dx * corr * (wb / sum);
  pos[bx + 1] -= dy * corr * (wb / sum);
  pos[bx + 2] -= dz * corr * (wb / sum);
}

function dist(pos, a, b) {
  return Math.hypot(pos[b * 3] - pos[a * 3], pos[b * 3 + 1] - pos[a * 3 + 1], pos[b * 3 + 2] - pos[a * 3 + 2]);
}

/** Angle at joint `j` between the incoming (a->j) and outgoing (j->b) bones. */
function angleBetween(pos, a, j, b) {
  const a3 = a * 3, j3 = j * 3, b3 = b * 3;
  let d1x = pos[j3] - pos[a3], d1y = pos[j3 + 1] - pos[a3 + 1], d1z = pos[j3 + 2] - pos[a3 + 2];
  let d2x = pos[b3] - pos[j3], d2y = pos[b3 + 1] - pos[j3 + 1], d2z = pos[b3 + 2] - pos[j3 + 2];
  const l1 = Math.hypot(d1x, d1y, d1z) || 1, l2 = Math.hypot(d2x, d2y, d2z) || 1;
  const c = (d1x * d2x + d1y * d2y + d1z * d2z) / (l1 * l2);
  return Math.acos(Math.max(-1, Math.min(1, c)));
}
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Accumulate a depenetration push taking the MAX along each direction, not the sum. */
function accumulatePush(corr, i, nx, ny, nz, mag) {
  if (mag <= 0) return;
  const o = i * 3;
  const dot = corr[o] * nx + corr[o + 1] * ny + corr[o + 2] * nz;
  if (dot >= mag) return;
  const add = mag - (dot > 0 ? dot : 0);
  corr[o] += nx * add; corr[o + 1] += ny * add; corr[o + 2] += nz * add;
}

function toVec(v, out) {
  if (!v) return out.set(0, 0, 0);
  if (Array.isArray(v)) return out.set(v[0] || 0, v[1] || 0, v[2] || 0);
  return out.set(v.x || 0, v.y || 0, v.z || 0);
}
function toQuat(q) {
  if (!q) return _identQ.set(0, 0, 0, 1);
  if (Array.isArray(q)) return _identQ.set(q[0], q[1], q[2], q[3]);
  return _identQ.set(q.x, q.y, q.z, q.w);
}

const _v0 = new THREE.Vector3();
const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _identQ = new THREE.Quaternion();
const _one = new THREE.Vector3(1, 1, 1);
const _up = new THREE.Vector3(0, 1, 0);
const _axis = new THREE.Vector3();
const _right = new THREE.Vector3();
const _front = new THREE.Vector3();
const _fwd = new THREE.Vector3(0, 0, 1);
const _m = new THREE.Matrix4();
const _segP = { x: 0, y: 0, z: 0 };
const _triP = { x: 0, y: 0, z: 0 };

export { DEFAULT_JOINTS, DEFAULT_BONES, DEFAULT_LIMITS, DEFAULT_LINKS };
